import { emptySplitApi } from './baseApi';
import { broadcastCacheInvalidator } from '../utils/broadcastCacheInvalidator';
import { GridRowId, GridFilterModel } from '@mui/x-data-grid-premium';
import { formatFilters } from '../utils/formatFilters';
import {
  AggregatedPerformanceReport,
  AdGroupBids,
} from '../types/bidManagement';

export const bidManagementSlice = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchReportsWithinDateRange: builder.query<
      {
        total: number;
        results: AggregatedPerformanceReport[];
        compare_results: AggregatedPerformanceReport[];
        ad_group_bids: AdGroupBids;
        totals?: AggregatedPerformanceReport;
        compare_totals?: AggregatedPerformanceReport;
        dih_earliest_date?: string;
      },
      {
        start_date: string;
        end_date: string;
        compare_start_date?: string;
        compare_end_date?: string;
        page: number;
        pageSize: number;
        filterModel?: GridFilterModel;
        sortBy?: string;
        sortByDirection?: 'ASC' | 'DESC';
        includeAccuracyReport?: boolean;
        reportType: string;
        combineMatchTypes?: boolean;
        deviceType?: string;
      }
    >({
      query: ({
        page,
        pageSize,
        filterModel,
        start_date,
        end_date,
        compare_start_date,
        compare_end_date,
        sortBy,
        sortByDirection,
        includeAccuracyReport,
        reportType,
        combineMatchTypes,
        deviceType,
      }) => {
        return {
          url: `${process.env.REACT_APP_API_ENDPOINT}/api/v1/multiplatform/bid_management/aggregated_reports`,
          method: 'POST',
          body: {
            start_date,
            end_date,
            compare_start_date,
            compare_end_date,
            filters: formatFilters(filterModel),
            page,
            page_size: pageSize,
            logic_operator: filterModel?.logicOperator || 'and',
            sort_by: sortBy,
            sort_by_direction: sortByDirection,
            include_accuracy_report: includeAccuracyReport,
            report_type: reportType,
            combine_match_types: combineMatchTypes,
            device_type: deviceType,
          },
        };
      },
      providesTags: ['AggregatedPerformanceReports'],
    }),
    exportAggregatedReports: builder.mutation<
      Blob,
      {
        start_date: string;
        end_date: string;
        compare_start_date?: string;
        compare_end_date?: string;
        filterModel?: GridFilterModel;
        sortBy?: string;
        sortByDirection?: 'ASC' | 'DESC';
        includeAccuracyReport?: boolean;
        reportType: string;
        combineMatchTypes?: boolean;
        view_type: string;
      }
    >({
      query: ({
        filterModel,
        start_date,
        end_date,
        compare_start_date,
        compare_end_date,
        sortBy,
        sortByDirection,
        includeAccuracyReport,
        reportType,
        combineMatchTypes,
        view_type,
      }) => ({
        url: `${process.env.REACT_APP_API_ENDPOINT}/api/v1/multiplatform/bid_management/export_aggregated_reports`,
        method: 'POST',
        body: {
          start_date,
          end_date,
          compare_start_date,
          compare_end_date,
          filters: formatFilters(filterModel),
          page: 0,
          page_size: 0,
          logic_operator: filterModel?.logicOperator || 'and',
          sort_by: sortBy,
          sort_by_direction: sortByDirection,
          include_accuracy_report: includeAccuracyReport,
          report_type: reportType,
          combine_match_types: combineMatchTypes,
          view_type: view_type,
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    updateBids: builder.mutation<
      void,
      {
        bidUpdates: {
          ad_group_id: GridRowId;
          bid: number;
          advertising_network: string;
          is_ad_vantage_managed: boolean;
        }[];
      }
    >({
      query: ({ bidUpdates }) => ({
        url: `${process.env.REACT_APP_API_ENDPOINT}/api/v1/multiplatform/bid_management/update_bids`,
        method: 'PUT',
        body: { bid_updates: bidUpdates },
      }),
      invalidatesTags: () =>
        broadcastCacheInvalidator([
          'AggregatedPerformanceReports',
          'AdGroups',
          'AdGroup',
        ]),
    }),
    updateBidPercentages: builder.mutation<
      void,
      {
        bidPercentages: {
          ad_group_id: GridRowId;
          bid_percentage: number;
          match_type: string;
        }[];
      }
    >({
      query: ({ bidPercentages }) => ({
        url: `/bid_management/update_percentages`,
        method: 'PUT',
        body: { bid_percentages: bidPercentages },
      }),
      invalidatesTags: () =>
        broadcastCacheInvalidator([
          'AggregatedPerformanceReports',
          'AdGroups',
          'AdGroup',
        ]),
    }),
  }),
});

export const {
  useFetchReportsWithinDateRangeQuery,
  useUpdateBidsMutation,
  useUpdateBidPercentagesMutation,
  useExportAggregatedReportsMutation,
} = bidManagementSlice;
